import { z } from 'zod';

import type {
  GetShipmentRequest,
  GetShipmentResponse,
  ShipmentDetail,
  ShipmentEvidenceSummary,
  ShipmentPresentationTotalsItem,
  ShipmentScaleTicketSummary,
} from './get-shipment.dto.js';
import {
  HoldingUnitLoadSessionGroupClaimStatusSchema,
  HoldingUnitLoadSessionStatusSchema,
  HoldingUnitWeightBreakdownSchema,
  HoldingUnitWeightDeltaSchema,
} from '../holding-units/holding-units.schema.js';
import {
  ShipmentHeaderSchema,
  ShipmentTotalsSummarySchema,
} from './shipments-shared.schema.js';
import { IsoDateSchema, IsoDateTimeSchema, PresentationTypeSchema, RcScopeTypeSchema } from './shipments-types.js';
import { ReportableWeightLbSchema } from '../reportable-weight.schema.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);

const ShipmentPresentationTotalsItemSchema: z.ZodType<ShipmentPresentationTotalsItem> = z
  .object({
    presentationType: PresentationTypeSchema,
    weightLb: nonNegativeNumberSchema.nullable(),
    pieces: nonNegativeIntSchema.nullable(),
    equivalentLbs: nonNegativeNumberSchema.nullable(),
  })
  .strict();

const ShipmentScaleTicketSummarySchema: z.ZodType<ShipmentScaleTicketSummary> = z
  .object({
    processorTicketCount: nonNegativeIntSchema,
    processorWeightLb: nonNegativeNumberSchema.nullable(),
  })
  .strict();

const ShipmentEvidenceSummarySchema: z.ZodType<ShipmentEvidenceSummary> = z
  .object({
    loadSessionId: z.uuid().nullable(),
    loadSessionAvailable: z.boolean(),
    loadSessionName: z.string().trim().min(1).nullable(),
    loadSessionStatus: HoldingUnitLoadSessionStatusSchema.nullable(),
    groupId: z.uuid().nullable(),
    groupAvailable: z.boolean(),
    groupMaterialId: z.uuid().nullable(),
    claimStatus: HoldingUnitLoadSessionGroupClaimStatusSchema.nullable(),
    unitCount: nonNegativeIntSchema.nullable(),
    firstWeighTotal: HoldingUnitWeightBreakdownSchema.nullable(),
    reweighTotal: HoldingUnitWeightBreakdownSchema.nullable(),
    differenceTotal: HoldingUnitWeightDeltaSchema.nullable(),
    officialForShipment: HoldingUnitWeightBreakdownSchema.nullable(),
    unresolvedDiscrepancyCount: nonNegativeIntSchema,
  })
  .strict();

const ShipmentDetailSchema = ShipmentHeaderSchema.extend({
  rcScopeType: RcScopeTypeSchema,
  createdAt: IsoDateTimeSchema,
  voidedAt: IsoDateTimeSchema.nullable(),
  receivedWeightLb: ReportableWeightLbSchema.nullable(),
  receiveDate: IsoDateSchema.nullable(),
  receiverDisplayName: z.string().trim().min(1),
}) satisfies z.ZodType<ShipmentDetail>;

export const GetShipmentRequestSchema: z.ZodType<GetShipmentRequest> = z
  .object({
    companyId: z.uuid(),
    shipmentId: z.uuid(),
  })
  .strict();

export const GetShipmentResponseSchema: z.ZodType<GetShipmentResponse> = z
  .object({
    shipment: ShipmentDetailSchema,
    totals: ShipmentTotalsSummarySchema,
    presentationTotals: z.array(ShipmentPresentationTotalsItemSchema),
    scaleTicketSummary: ShipmentScaleTicketSummarySchema,
    evidenceSummary: ShipmentEvidenceSummarySchema,
  })
  .strict();
